/**
 * Transaction History View
 */

define(function(require, exports, module) {
    'use strict';

    const core = require('core');
    const i18n = require('i18n');
    const _ = require('underscore');
    const Form = require('class/form.class');
    const device = require('util/device.util');
    const PortletView = require('modules/portlet/portlet.view');
    const dateMixin = require('portlets/registration/mixins/date.mixin');
    const transactionHistoryConfig = require('portlets/cashier/transaction-history/transaction-history.config');
    const TransactionHistoryCollection = require('portlets/cashier/transaction-history/transaction-history.collection');

    // widgets
    require('widgets/accordion.widget');

    module.exports = PortletView.extend({

        mixins: [dateMixin],

        accessRules: {
            allow: ['user']
        },

        originalEvents: {
            'submit form': 'handleLoadTransaction',
            'tap .fn-load-more': 'handleLoadMore',
            'click .fn-export-link': 'handleExportClick',
            'change .fn-transaction-select': 'handleTransactionPeriodChange',
            'change .fn-group': 'handleLoadTransaction',
            'change .fn-product-type': 'renderTransactions',
            'change .fn-transaction-type': '_handleFilterChange',
            'change .fn-transaction-status': '_handleFilterChange',
            'tap .fn-tabs-group': 'handleChangeTabGroup',
            'keydown .fn-tabs-group': 'onTabGroupKeyDown'
        },

        events: function() {
            return _.extend({}, PortletView.prototype.events, this.originalEvents, this.additionalEvents);
        },

        initialize: function(options) {
            this.options = options;
            this._setExportSupport(options);
            this.collection = new TransactionHistoryCollection(options.config);
            this.attachWidget(this.$el.accordion());
        },

        subscribe: function() {
            this.listenTo(core, 'transaction-total:update', this._updateTotalAmounts.bind(this));
        },

        render: function() {
            this.$el.html(this.tmpl('transaction-history', this.options.config));

            this.form = new Form(this.$('.fn-form'), {
                validationConfig: transactionHistoryConfig(Boolean(this.options.config.isDetailedView))
            });

            if (!this.options.config.isDetailedView && this.options.config.showGroupAs === 'tabs') {
                this.form.data('group', this.$('.fn-tabs-group.active').data('group'));
            }

            this._setInitialDate();
            this._loadTransactions();

            return this;
        },

        template: function() {
            var config = this.options.config;
            var layout = 'transaction-history.items';

            if (config.isDetailedView) {
                layout += device.formFactor === 'mobile' ? '.detailed-mobile' : '.detailed';
            } else if (config.transactionsLayout !== 'SIMPLE') {
                layout += '.extended';
            }

            return layout;
        },

        /**
         * Define and setting dates to form elements(another period section)
         * @private
         */
        _setInitialDate: function() {
            var normalizeDate = function(date) {
                return String(date).length === 1 ? '0' + date : String(date);
            };

            var endDate = new Date();
            var startDate = new Date();
            var startTime = this.options.config.isDetailedView
                ? Number(startDate) - 6 * 24 * 60 * 60 * 1000 // 6 days ago
                : Number(startDate) - 89 * 24 * 60 * 60 * 1000; // 89 days ago

            startDate.setTime(startTime);

            this.form.data({
                day_endDate: normalizeDate(endDate.getDate()),
                month_endDate: normalizeDate(endDate.getMonth() + 1),
                year_endDate: String(endDate.getFullYear()),
                day_startDate: normalizeDate(startDate.getDate()),
                month_startDate: normalizeDate(startDate.getMonth() + 1),
                year_startDate: String(startDate.getFullYear())
            });
        },

        /**
         * Launch form validation, if success, launch _loadTransactions
         * @param {Event} event
         * @param {Object} options
         */
        handleLoadTransaction: function(event, options) {
            event.preventDefault();

            this.form.validate().done(this._loadTransactions.bind(this, options));
        },

        /**
         * Run _loadTransactions with specific params
         */
        handleLoadMore: function() {
            var itemsCount = this.collection.length;

            this._loadTransactions({
                remove: false,
                ...this.collection.lastStartEndDates
            }).done(function() {
                var $lastItem = this.$(`.fn-transaction:nth-child(${itemsCount}) a`);

                if ($lastItem && $lastItem[0]) {
                    $lastItem[0].focus();
                }
            }.bind(this));
        },

        handleChangeTabGroup: function(e) {
            if (!this.form.loading && this.form.data('group') !== e.currentTarget.dataset.group) {
                this.form.data('group', e.currentTarget.dataset.group);
                this.$('.fn-tabs-group').removeClass('active');
                e.currentTarget.classList.add('active');

                this.handleLoadTransaction(e);
            }
        },

        onTabGroupKeyDown: function(event) {
            var { key } = event;

            if (key === 'Enter') {
                event.preventDefault();

                this.handleChangeTabGroup(event);
            }
        },

        /**
         * Toggle block with transactions
         * @param {Event} event
         */
        handleTransactionPeriodChange: function(event) {
            this.$('.fn-another-period-block').toggleClass('hidden', event.target.value !== 'another_period');

            if (event.target.value !== 'another_period') {
                this.handleLoadTransaction(event, {
                    ...device.formFactor === 'desktop' && { elementToFocus: event.target }
                });
            }
        },

        /**
         * Get transactions from the server
         * @param {Object} [options]
         * @private
         */
        _loadTransactions: function(options) {
            options = _.extend({}, this._getFormData(), options);

            if (this.options.config.isDetailedView) {
                _.extend(options, { reset: true });
            }

            this.form.showLoading();
            this.$('.fn-export-link').addClass('btn_state_disabled');

            return this.collection.fetch(options)
                .always(() => {
                    this.form.hideLoading(this.form);
                    options.elementToFocus?.focus();
                })
                .done(function fetchDone(opts) {
                    this.renderTransactions(opts);
                    this._updateTotalAmounts();
                }.bind(this, options))
                .fail(this.showError.bind(this));
        },

        /**
         * Collect data from the form depend on another period option, setting start and end date,
         * @returns {{group: {Object}, startDate: Date, endDate: Date}} returning transaction group, start and end date
         * @private
         */
        _getFormData: function() {
            var data = this.form.data();
            var startDate, endDate;

            if (data.period === 'another_period') {
                startDate = new Date(Date.UTC(data.year_startDate, data.month_startDate - 1, data.day_startDate));
                endDate = new Date(Date.UTC(
                    data.year_endDate,
                    data.month_endDate - 1,
                    data.day_endDate,
                    // specify end of the day
                    '23',
                    '59',
                    '59'
                ));
            } else {
                endDate = new Date();

                if (Number(data.period) === 89) {
                    endDate = new Date();
                    startDate = this.get3MonthsBefore(endDate);
                } else {
                    startDate = new Date(endDate);
                    startDate.setDate(startDate.getDate() - Number(data.period));
                }
            }

            return {
                group: data.group,
                startDate,
                endDate
            };
        },

        get3MonthsBefore: function(now = new Date()) {
            const hour = now.getUTCHours();
            const resultDay = new Date(now.getTime());

            resultDay.setDate(1);
            resultDay.setMonth(now.getMonth() - 3);

            const daysInMonth = new Date(resultDay.getFullYear(), resultDay.getMonth() + 1, 0).getDate();

            if (daysInMonth < now.getDate()) {
                resultDay.setMonth(resultDay.getMonth() + 1);
            } else {
                resultDay.setDate(now.getDate());
            }

            resultDay.setUTCHours(hour);

            return resultDay;
        },

        _handleFilterChange: function(event) {
            event.preventDefault();

            return this.options.config.isDetailedView
                ? this._loadTransactions({
                    transactionType: this.form.data('transactionType'),
                    transactionStatus: this.form.data('transactionStatus'),
                    ...device.formFactor === 'desktop' && { elementToFocus: event.target }
                })
                : this.renderTransactions(this._getFormData());
        },

        /**
         * Append or replace HTML with transactions and toggle 'Load more' button
         * @param {Object} options
         * @private
         */
        renderTransactions: function(options) {
            var html = '';
            var collection = this.collection.toJSON();
            var productType = this.form.data('productType');
            var transactionType = this.form.data('transactionType');
            var transactionStatus = this.form.data('transactionStatus');

            if (this.options.config.isDetailedView) {
                transactionType = transactionType || options.group;
            }

            if (productType) {
                if (!options.eventPhase) { // exclude fake trigger change event
                    productType = this._updateProductTypes(productType);
                }

                if (productType !== 'all') {
                    collection = _.where(collection, {
                        clientType: productType
                    });
                }
            }

            if (transactionType) {
                if (!options.eventPhase && !this.options.config.isDetailedView) { // exclude fake trigger change event
                    transactionType = this._updateTransactionTypes(transactionType);
                }

                if (transactionType !== 'all') {
                    collection = _.where(collection, {
                        actionType: transactionType
                    });
                }
            }

            if (transactionStatus) {
                if (!options.eventPhase && !this.options.config.isDetailedView) { // exclude fake trigger change event
                    transactionStatus = this._updateTransactionStatus(transactionStatus);
                }

                if (transactionStatus !== 'all') {
                    collection = _.where(collection, {
                        status: transactionStatus
                    });
                }
            }

            if (collection.length) {
                html = this.tmpl(this.template(), collection);
                this.$('.fn-show-more').toggleClass('hidden', !this.collection.cachedPageId);

                if (this.options.config.isDetailedView) {
                    this.generateLink(collection);
                }
            } else {
                html = this._renderNoTransactions(options.eventPhase ? this._getFormData() : options);
                this.$('.fn-show-more').addClass('hidden');
            }

            this.$('.fn-export-link').toggleClass('btn_state_disabled', !collection.length);
            this.$('.fn-transaction-list').html(html);
        },

        _updateProductTypes: function(productType) {
            var productTypes = _.uniq(this.collection.pluck('clientType')),
                $container = this.$('#productType'),
                _productType = 'all';

            $container.find('option:not(:first-child)').remove();
            $container.find('option[value="all"]').attr('selected', true);

            productTypes.forEach(function(product) {
                var option = document.createElement('option');

                option.setAttribute('value', product);

                // for 'Load more' case
                if (product === productType) {
                    option.setAttribute('selected', true);
                    _productType = productType;
                }

                option.textContent = option.innerText = product;

                $container.append(option);
            });

            Form.refreshSelectField($container);

            return _productType;
        },

        _updateTransactionTypes: function(transactionType) {
            var collection = this.collection.toJSON();
            var typeNames = _.uniq(_.pluck(collection, 'typeName'));
            var actionTypes = _.uniq(_.pluck(collection, 'actionType'));
            actionTypes = _.compact(actionTypes).length ? actionTypes : typeNames;
            var $container = this.$('#transactionType');
            var _transactionType = 'all';

            $container.find('option:not(:first-child)').remove();
            $container.find('option[value="all"]').attr('selected', true);

            actionTypes.forEach(function(actionType, index) {
                var option = document.createElement('option');

                option.setAttribute('value', actionType);

                // for 'Load more' case
                if (actionType === transactionType) {
                    option.setAttribute('selected', true);
                    _transactionType = transactionType;
                }

                option.textContent = option.innerText = typeNames[index];

                $container.append(option);
            });

            Form.refreshSelectField($container);

            return _transactionType;
        },

        _updateTransactionStatus: function(transactionStatus) {
            var collection = this.collection.toJSON();
            var typeNames = _.uniq(_.pluck(collection, 'status'));
            var $container = this.$('#transactionStatus');
            var _transactionStatus = 'all';

            $container.find('option:not(:first-child)').remove();
            $container.find('option[value="all"]').attr('selected', true);

            typeNames.forEach(function(typeName, index) {
                var option = document.createElement('option');

                option.setAttribute('value', typeName);

                // for 'Load more' case
                if (typeName === transactionStatus) {
                    option.setAttribute('selected', true);
                    _transactionStatus = transactionStatus;
                }

                option.textContent = option.innerText = typeNames[index];

                $container.append(option);
            });

            Form.refreshSelectField($container);

            return _transactionStatus;
        },

        /**
         * Return HTML with error message when transactions not found
         * @param {Object} options
         * @returns {String}
         * @private
         */
        _renderNoTransactions: function(options) {
            var message = this.translate('playtech.mobile.system.mobile-cashier.transactions.no.transactions') + ' ' +
                window.Playtech.API.formatter.getDate(options.startDate) + ' - ' +
                window.Playtech.API.formatter.getDate(options.endDate);

            return this.tmpl('cashier.error', {message: message});
        },

        _updateTotalAmounts: function() {
            var summaryAmounts = this.collection.getSummaryAmounts();
            var options, tmpl;

            if (!summaryAmounts || !summaryAmounts.totalWins) {
                return;
            }

            options = _.extend({}, summaryAmounts, {
                formFactor: device.formFactor
            });
            tmpl = this.tmpl('transaction-history.total-amounts', options);
            this.el.querySelector('.fn-total-amounts').innerHTML = tmpl;
        },

        generateLink: function(models) {
            if (!this.collection.length || !this.options.config.isExportSupport) {
                return;
            }

            var fileName = this.options.config.isDetailedView
                ? 'detailed_transaction_history_'
                : 'transaction_history_';

            this.$('.fn-export-link').attr({
                download: fileName +
                    window.Playtech.API.formatter.getDateForCsv(this.collection.startDate) + '_' +
                    window.Playtech.API.formatter.getDateForCsv(this.collection.endDate) + '.csv',
                href: this.collection.generateCsv(models)
            });
        },

        handleExportClick: function(event) {
            event.stopImmediatePropagation();

            if (this.options.config.isDetailedView && this.collection.length && this.options.config.isExportSupport) {
                return;
            }

            if (!this.options.config.isExportSupport || this.options.config.isDetailedView && !this.collection.length) {
                event.preventDefault();

                return;
            }

            this.$('.fn-export-link').html(i18n.t('playtech.mobile.system.mobile-cashier.transactions.loading'));
            this.$('.fn-export-link').attr('disabled', 'disabled');

            const link = document.createElement('a');

            link.setAttribute('download', 'transaction_history_' +
                window.Playtech.API.formatter.getDateForCsv(window.Playtech.API.formatter.getDateForIms(this.collection.startDate)) + '_' +
                window.Playtech.API.formatter.getDateForCsv(window.Playtech.API.formatter.getDateForIms(this.collection.endDate)) + '.csv');

            this.collection.getAllTransactionsForExport(this._getFormData())
                .then(data => {
                    link.setAttribute('href', this.collection.generateCsv(data));
                    link.click();

                    this.$('.fn-export-link').html(i18n.t('playtech.mobile.system.mobile-cashier.transactions.export'));
                    this.$('.fn-export-link').removeAttr('disabled');
                });
        },

        _setExportSupport: function(options) {
            var link = document.createElement('a');

            if (typeof link.download !== 'undefined') {
                options.config.isExportSupport = true;
            }
        }
    });
});
